import { useCallback, useEffect, useState } from "react";
import { VenueData, VenueHistoryItem } from "../types/venue";

const HISTORY_STORAGE_KEY = "ticket-app:venue-history";
const HISTORY_LIMIT = 10;

const sanitize = (value?: string | null) => value?.trim() ?? "";

const createVenueKey = (venue: VenueData) =>
	[
		sanitize(venue.venueName).toLowerCase(),
		sanitize(venue.postalCode).replace(/-/g, ""),
		sanitize(venue.prefecture).toLowerCase(),
		sanitize(venue.city).toLowerCase(),
		sanitize(venue.address).toLowerCase(),
	].join("|");

const hasMinimumVenueData = (venue: VenueData) =>
	sanitize(venue.postalCode) &&
	sanitize(venue.prefecture) &&
	sanitize(venue.city) &&
	sanitize(venue.address);

const readHistory = (): VenueHistoryItem[] => {
	if (typeof window === "undefined") {
		return [];
	}

	try {
		const raw = window.localStorage.getItem(HISTORY_STORAGE_KEY);
		if (!raw) {
			return [];
		}

		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) {
			return [];
		}

		return parsed.filter((item): item is VenueHistoryItem => {
			if (!item || typeof item !== "object") {
				return false;
			}
			const casted = item as Partial<VenueHistoryItem>;
			return (
				typeof casted.id === "string" &&
				typeof casted.lastUsedAt === "number" &&
				typeof casted.venueName === "string" &&
				typeof casted.postalCode === "string" &&
				typeof casted.prefecture === "string" &&
				typeof casted.city === "string" &&
				typeof casted.address === "string"
			);
		});
	} catch (error) {
		console.error("Failed to read venue history", error);
		return [];
	}
};

const persistHistory = (history: VenueHistoryItem[]) => {
	if (typeof window === "undefined") {
		return;
	}

	try {
		window.localStorage.setItem(
			HISTORY_STORAGE_KEY,
			JSON.stringify(history.slice(0, HISTORY_LIMIT))
		);
	} catch (error) {
		console.error("Failed to save venue history", error);
	}
};

export const useVenueHistory = () => {
	const [venueHistory, setVenueHistory] = useState<VenueHistoryItem[]>([]);

	useEffect(() => {
		setVenueHistory(readHistory());
	}, []);

	const updateHistory = useCallback(
		(updater: (prev: VenueHistoryItem[]) => VenueHistoryItem[]) => {
			setVenueHistory((prev) => {
				const next = updater(prev);
				persistHistory(next);
				return next;
			});
		},
		[]
	);

	const addVenueToHistory = useCallback(
		(venue: VenueData) => {
			if (!hasMinimumVenueData(venue)) {
				return;
			}

			updateHistory((prev) => {
				const key = createVenueKey(venue);
				const filtered = prev.filter((item) => item.id !== key);
				const nextItem: VenueHistoryItem = {
					...venue,
					id: key,
					lastUsedAt: Date.now(),
				};
				return [nextItem, ...filtered].slice(0, HISTORY_LIMIT);
			});
		},
		[updateHistory]
	);

	return { venueHistory, addVenueToHistory };
};

export type { VenueData, VenueHistoryItem } from "../types/venue";
